import { ExtranetOrderScheduleCustomerGroupDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacText } from '@pharmac/ui'
import { weekdayShortTranslations } from '../customer-groups/util/date-utils'

type Props = {
  customerGroups: ExtranetOrderScheduleCustomerGroupDto[]
}

export const OrderSchedulesCustomerGroupTable = ({ customerGroups }: Props) => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()

  const getWeekDays = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.getDay() - 1
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

    return String(t(weekdayShortTranslations(weekDays[day]) as any)).toLocaleUpperCase()
  }

  const countEstimateIsZero = (count: number) => {
    if (count === 0) return '-'

    return count
  }

  return (
    <ExtranetTable>
      {minDesktop && (
        <ExtranetTableRow
          style={{
            backgroundColor: 'var(--extranet-light-purple)',
            border: 'none',
            fontWeight: 600,
          }}
        >
          <ExtranetTableColumn size={0.1} />
          <ExtranetTableColumn size={4}>
            {t('extranet:orderHistory.customerGroup')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={2}>
            {t('extranet:orderSchedules.orderPrediction')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={4}>
            {t('extranet:orderSchedules.exceptions')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={2}>
            {t('extranet:orderSchedules.periodLength')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={4}>
            {t('extranet:orderSchedules.atThePharmacy')}
          </ExtranetTableColumn>
        </ExtranetTableRow>
      )}
      {customerGroups?.map((group) => (
        <ExtranetTableRow
          key={group.groupId}
          style={{
            opacity:
              group.isSkipped ||
              (group.exceptions.length > 0 &&
                !group.exceptions?.map((a) => a.exceptionDeadlineDate).includes(group.deadlineDate))
                ? 0.8
                : undefined,
            backgroundColor:
              group.isSkipped ||
              (group.exceptions.length !== 0 &&
                !group.exceptions?.map((a) => a.exceptionDeadlineDate).includes(group.deadlineDate))
                ? 'var(--color-accent-2)'
                : 'var(--color-accent-1)',
          }}
          expand={
            <>
              {!minDesktop && (
                <ExtranetContainer
                  paddingSmall
                  marginSmallBottom
                  flexColumn
                  paddingMediumLeft
                >
                  <ExtranetContainer
                    full
                    flexColumn
                  >
                    <PharmacText weight="bold">
                      {t('extranet:orderSchedules.exceptions')}
                    </PharmacText>
                    <ExtranetContainer
                      flexRow
                      alignCenter
                      paddingSmallTop
                    >
                      <ExtranetContainer paddingSmallRight>
                        <PharmacIcon
                          icon={group.exceptions.length !== 0 ? 'chevronsRight' : 'check'}
                          style={{
                            color:
                              group.isSkipped ||
                              (group.exceptions.length !== 0 &&
                                group.exceptions?.map(
                                  (a) => a.exceptionDeadlineDate !== group.deadlineDate
                                ))
                                ? undefined
                                : group.exceptions.length !== 0
                                  ? 'var(--color-warning)'
                                  : 'var(--color-success)',
                          }}
                        />
                      </ExtranetContainer>
                      <ExtranetContainer flexColumn>
                        {!group.isDouble && !group.isSkipped && group.exceptions.length === 0 && (
                          <PharmacText>{t('extranet:orderSchedules.noExceptions')}</PharmacText>
                        )}
                        {group.isSkipped && (
                          <PharmacText>{t('extranet:orderSchedules.removedFromDate')}</PharmacText>
                        )}
                        {group.exceptions?.every(
                          (a) => a.originalDeadlineDate === group.deadlineDate
                        ) &&
                          group.isDouble &&
                          !group.isSkipped && (
                            <>
                              <PharmacText>
                                {t('extranet:orderSchedules.doubledOffDate')}
                              </PharmacText>
                              <PharmacText style={{ fontSize: '13px' }}>
                                {t('extranet:orderSchedules.expectionDate')}:{' '}
                                {group.exceptions.length !== 0 &&
                                  group.exceptions?.map((a) => (
                                    <>
                                      {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                      {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                      {a.exceptionDeadlineTime}{' '}
                                    </>
                                  ))}
                              </PharmacText>
                            </>
                          )}
                        {group.exceptions.length > 1 &&
                          group.exceptions?.find(
                            (a) => a.originalDeadlineDate === group.deadlineDate
                          ) &&
                          group.isDouble &&
                          !group.isSkipped && (
                            <>
                              <PharmacText>
                                {t('extranet:orderSchedules.doubledOffDate')}
                              </PharmacText>
                              <PharmacText style={{ fontSize: '13px' }}>
                                {t('extranet:orderSchedules.expectionDate')}:{' '}
                                {group.exceptions
                                  ?.map((a) => (
                                    <>
                                      {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                      {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                      {a.exceptionDeadlineTime}{' '}
                                    </>
                                  ))
                                  .at(0)}
                              </PharmacText>
                            </>
                          )}
                        {group.exceptions?.every(
                          (a) => a.originalDeadlineDate !== group.deadlineDate
                        ) &&
                          group.isDouble &&
                          !group.isSkipped && (
                            <>
                              <PharmacText>{t('extranet:orderSchedules.double')}</PharmacText>
                              <ExtranetContainer flexRow>
                                <PharmacText style={{ fontSize: '13px' }}>
                                  {group.exceptions.length === 1 ? (
                                    <>{t('extranet:orderSchedules.doubledFromDate')}: </>
                                  ) : (
                                    <>{t('extranet:orderSchedules.doubledFromDates')}: </>
                                  )}{' '}
                                  {group.exceptions.map((a) => (
                                    <>
                                      {getWeekDays(a.originalDeadlineDate)}{' '}
                                      {formatDateString(a.originalDeadlineDate)}{' '}
                                    </>
                                  ))}
                                </PharmacText>
                              </ExtranetContainer>
                            </>
                          )}
                        {group.exceptions?.find(
                          (a) => a.originalDeadlineDate === group.deadlineDate
                        ) &&
                          !group.isDouble &&
                          !group.isSkipped && (
                            <>
                              <PharmacText>
                                {t('extranet:orderSchedules.transferedOffDate')}
                              </PharmacText>
                              <PharmacText style={{ fontSize: '13px' }}>
                                {t('extranet:orderSchedules.expectionDate')}:{' '}
                                {group.exceptions.length !== 0 &&
                                  group.exceptions?.map((a) => (
                                    <>
                                      {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                      {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                      {a.exceptionDeadlineTime}{' '}
                                    </>
                                  ))}
                              </PharmacText>
                            </>
                          )}
                        {group.exceptions?.find(
                          (a) => a.originalDeadlineDate !== group.deadlineDate
                        ) &&
                          !group.isDouble &&
                          !group.isSkipped && (
                            <>
                              <PharmacText>{t('extranet:orderSchedules.transfer')}</PharmacText>
                              <PharmacText style={{ fontSize: '13px' }}>
                                {t('extranet:orderSchedules.movedFromDate')}:{' '}
                                {group.exceptions.map((a) => (
                                  <>
                                    {getWeekDays(a.originalDeadlineDate)}{' '}
                                    {formatDateString(a.originalDeadlineDate)}{' '}
                                  </>
                                ))}
                              </PharmacText>
                            </>
                          )}
                      </ExtranetContainer>
                    </ExtranetContainer>
                  </ExtranetContainer>
                  <ExtranetContainer
                    full
                    flexColumn
                    paddingMediumTop
                  >
                    <PharmacText weight="bold">
                      {t('extranet:orderSchedules.periodLength')}
                    </PharmacText>
                    <PharmacText paddingSmallTop>
                      {group.cycleLength} {t('extranet:orderSchedules.days')}
                    </PharmacText>
                  </ExtranetContainer>
                  <ExtranetContainer
                    full
                    flexColumn
                    paddingMediumTop
                  >
                    <PharmacText weight="bold">
                      {t('extranet:orderSchedules.atThePharmacy')}
                    </PharmacText>
                  </ExtranetContainer>
                  <ExtranetContainer
                    full
                    paddingSmallTop
                  >
                    {group.estimatedDeliveredDate.map((date, i) => (
                      <ExtranetContainer>
                        <PharmacText marginSmallRight>{getWeekDays(date)}</PharmacText>
                        <PharmacText>{formatDateString(date)}</PharmacText>
                        {group.estimatedDeliveredDate.length > 1 && i === 0 && (
                          <PharmacText paddingSmallHorizontal>/</PharmacText>
                        )}
                      </ExtranetContainer>
                    ))}
                  </ExtranetContainer>
                </ExtranetContainer>
              )}
            </>
          }
          toggleExpand={() => null}
          children={({ isExpanded }) => (
            <>
              <ExtranetTableColumn
                size={minDesktop ? 0.2 : 0.3}
                style={{
                  justifyContent: 'center',
                  backgroundColor:
                    group.isSkipped ||
                    (group.exceptions.length !== 0 &&
                      !group.exceptions
                        ?.map((a) => a.exceptionDeadlineDate)
                        .includes(group.deadlineDate))
                      ? 'var(--color-accent-4)'
                      : group.exceptions.length !== 0
                        ? 'var(--color-warning)'
                        : 'var(--color-success)',
                }}
              />
              <ExtranetTableColumn size={minDesktop ? 4 : 6}>
                <PharmacText paddingSmallLeft>{group.groupName}</PharmacText>
              </ExtranetTableColumn>
              <ExtranetTableColumn size={minDesktop ? 2 : 2}>
                <ExtranetContainer
                  alignCenter
                  flexRow
                >
                  <PharmacIcon
                    icon="user"
                    style={{
                      color:
                        group.isSkipped ||
                        (group.exceptions.length !== 0 &&
                          !group.exceptions
                            ?.map((a) => a.exceptionDeadlineDate)
                            .includes(group.deadlineDate))
                          ? undefined
                          : 'var(--color-primary)',
                    }}
                  />
                  <PharmacText paddingSmallLeft>
                    {countEstimateIsZero(group.orderCountEstimate)}
                  </PharmacText>
                </ExtranetContainer>
              </ExtranetTableColumn>
              {minDesktop && (
                <ExtranetTableColumn size={4}>
                  <ExtranetContainer
                    alignCenter
                    flexRow
                    full
                  >
                    <ExtranetContainer paddingSmallRight>
                      <PharmacIcon
                        icon={
                          group.isSkipped || group.isDouble
                            ? 'chevronsRight'
                            : group.exceptions.length !== 0
                              ? 'chevronsRight'
                              : 'check'
                        }
                        style={{
                          color:
                            group.isSkipped ||
                            (group.exceptions.length !== 0 &&
                              !group.exceptions
                                ?.map((a) => a.exceptionDeadlineDate)
                                .includes(group.deadlineDate))
                              ? undefined
                              : group.exceptions.length !== 0
                                ? 'var(--color-warning)'
                                : 'var(--color-success)',
                        }}
                      />
                    </ExtranetContainer>
                    <ExtranetContainer flexColumn>
                      {!group.isDouble && !group.isSkipped && group.exceptions.length === 0 && (
                        <PharmacText>{t('extranet:orderSchedules.noExceptions')}</PharmacText>
                      )}
                      {group.isSkipped && (
                        <PharmacText>{t('extranet:orderSchedules.removedFromDate')}</PharmacText>
                      )}
                      {group.exceptions?.every(
                        (a) => a.originalDeadlineDate === group.deadlineDate
                      ) &&
                        group.isDouble &&
                        !group.isSkipped && (
                          <>
                            <PharmacText>{t('extranet:orderSchedules.doubledOffDate')}</PharmacText>
                            <PharmacText style={{ fontSize: '13px' }}>
                              {t('extranet:orderSchedules.expectionDate')}:{' '}
                              {group.exceptions.length !== 0 &&
                                group.exceptions?.map((a) => (
                                  <>
                                    {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                    {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                    {a.exceptionDeadlineTime}{' '}
                                  </>
                                ))}
                            </PharmacText>
                          </>
                        )}
                      {group.exceptions.length > 1 &&
                        group.exceptions?.find(
                          (a) => a.originalDeadlineDate === group.deadlineDate
                        ) &&
                        group.isDouble &&
                        !group.isSkipped && (
                          <>
                            <PharmacText>{t('extranet:orderSchedules.doubledOffDate')}</PharmacText>
                            <PharmacText style={{ fontSize: '13px' }}>
                              {t('extranet:orderSchedules.expectionDate')}:{' '}
                              {group.exceptions
                                ?.map((a) => (
                                  <>
                                    {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                    {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                    {a.exceptionDeadlineTime}{' '}
                                  </>
                                ))
                                .at(0)}
                            </PharmacText>
                          </>
                        )}
                      {group.exceptions?.every(
                        (a) => a.originalDeadlineDate !== group.deadlineDate
                      ) &&
                        group.isDouble &&
                        !group.isSkipped && (
                          <>
                            <PharmacText>{t('extranet:orderSchedules.double')}</PharmacText>
                            <ExtranetContainer flexRow>
                              <PharmacText style={{ fontSize: '13px' }}>
                                {group.exceptions.length === 1 ? (
                                  <>{t('extranet:orderSchedules.doubledFromDate')}: </>
                                ) : (
                                  <>{t('extranet:orderSchedules.doubledFromDates')}: </>
                                )}{' '}
                                {group.exceptions.map((a) => (
                                  <>
                                    {getWeekDays(a.originalDeadlineDate)}{' '}
                                    {formatDateString(a.originalDeadlineDate)}{' '}
                                  </>
                                ))}
                              </PharmacText>
                            </ExtranetContainer>
                          </>
                        )}
                      {group.exceptions?.find(
                        (a) => a.originalDeadlineDate === group.deadlineDate
                      ) &&
                        !group.isDouble &&
                        !group.isSkipped && (
                          <>
                            <PharmacText>
                              {t('extranet:orderSchedules.transferedOffDate')}
                            </PharmacText>
                            <PharmacText style={{ fontSize: '13px' }}>
                              {t('extranet:orderSchedules.expectionDate')}:{' '}
                              {group.exceptions.length !== 0 &&
                                group.exceptions?.map((a) => (
                                  <>
                                    {getWeekDays(a.exceptionDeadlineDate)}{' '}
                                    {formatDateString(String(a.exceptionDeadlineDate))}{' '}
                                    {a.exceptionDeadlineTime}{' '}
                                  </>
                                ))}
                            </PharmacText>
                          </>
                        )}
                      {group.exceptions?.find(
                        (a) => a.originalDeadlineDate !== group.deadlineDate
                      ) &&
                        !group.isDouble &&
                        !group.isSkipped && (
                          <>
                            <PharmacText>{t('extranet:orderSchedules.transfer')}</PharmacText>
                            <PharmacText style={{ fontSize: '13px' }}>
                              {t('extranet:orderSchedules.movedFromDate')}:{' '}
                              {group.exceptions.map((a) => (
                                <>
                                  {getWeekDays(a.originalDeadlineDate)}{' '}
                                  {formatDateString(a.originalDeadlineDate)}{' '}
                                </>
                              ))}
                            </PharmacText>
                          </>
                        )}
                    </ExtranetContainer>
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn size={2}>
                  {!group.isSkipped && (
                    <PharmacText>
                      {group.cycleLength} {t('extranet:orderSchedules.days')}
                    </PharmacText>
                  )}
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn size={4}>
                  <ExtranetContainer
                    flexRow
                    full
                  >
                    {!group.exceptions?.find(
                      (a) => a.originalDeadlineDate === group.deadlineDate
                    ) &&
                      group.estimatedDeliveredDate.map((date, i) => (
                        <ExtranetContainer key={i}>
                          <PharmacText marginSmallRight>{getWeekDays(date)}</PharmacText>
                          <PharmacText>{formatDateString(date)}</PharmacText>
                          {group.estimatedDeliveredDate.length > 1 && i === 0 && (
                            <PharmacText paddingSmallHorizontal>/</PharmacText>
                          )}
                        </ExtranetContainer>
                      ))}
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {!minDesktop && (
                <ExtranetExpandArrowColumn>
                  <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                </ExtranetExpandArrowColumn>
              )}
            </>
          )}
        />
      ))}
    </ExtranetTable>
  )
}
